import React, { useRef, useState } from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import StorefrontTwoToneIcon from "@mui/icons-material/StorefrontTwoTone";
import MeetingRoomTwoToneIcon from "@mui/icons-material/MeetingRoomTwoTone";
import QrCode2TwoToneIcon from "@mui/icons-material/QrCode2TwoTone";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import DownloadTwoToneIcon from "@mui/icons-material/DownloadTwoTone";
import PrintTwoToneIcon from "@mui/icons-material/PrintTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInTwoToneIcon from "@mui/icons-material/ZoomInTwoTone";
import BadgeTwoToneIcon from "@mui/icons-material/BadgeTwoTone";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import type { IOfflineStall } from "../../type/offlineExhibitorType";
import ExhibitorStallPassCard, {
	downloadStallPassPNG,
	getStallQrCodeUrl,
} from "./ExhibitorStallPassCard";

interface Props {
	stalls?: IOfflineStall[];
	exhibitorName?: string;
}

const ExhibitorStallsTab: React.FC<Props> = ({ stalls, exhibitorName }) => {
	const theme = useTheme();
	const passCardRef = useRef<HTMLDivElement>(null);
	const [selectedStall, setSelectedStall] = useState<IOfflineStall | null>(null);
	const [isDownloading, setIsDownloading] = useState<boolean>(false);

	const handleDownloadQR = async (stallNo: string) => {
		const qrUrl = getStallQrCodeUrl(stallNo, 600);
		try {
			const res = await fetch(qrUrl);
			const blob = await res.blob();
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = `stall-qr-${stallNo}.png`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(link.href);
		} catch {
			window.open(qrUrl, "_blank");
		}
	};

	const handleDownloadPass = async () => {
		if (!selectedStall || !passCardRef.current) return;
		try {
			setIsDownloading(true);
			await downloadStallPassPNG(selectedStall, passCardRef.current);
		} finally {
			setIsDownloading(false);
		}
	};

	const handlePrint = useReactToPrint({
		content: () => passCardRef.current,
		documentTitle: `BTTF-Stall-Pass-${selectedStall?.stall_no || "pass"}`,
		pageStyle: `
			@page {
				size: auto;
				margin: 0mm;
			}
			@media print {
				html, body {
					margin: 0 !important;
					padding: 0 !important;
					-webkit-print-color-adjust: exact !important;
					print-color-adjust: exact !important;
				}
				body {
					display: flex !important;
					justify-content: center !important;
					align-items: center !important;
					min-height: 100vh !important;
					background: #ffffff !important;
					padding: 10mm 0 !important;
				}
				.stall-pass-card-container,
				.exhibitor-stall-pass-card {
					width: 100% !important;
					margin: 0 auto !important;
					box-shadow: none !important;
					page-break-inside: avoid !important;
					break-inside: avoid !important;
				}
			}
		`,
	});

	return (
		<>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow
							sx={{
								bgcolor: (theme) =>
									alpha(theme.palette.primary.main, 0.04),
							}}
						>
							<TableCell
								sx={{ fontWeight: 700, width: 70, color: "text.primary" }}
							>
								SL
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Hall Name
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Stall No
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								QR Code
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Total Scanned
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Registration Date
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{stalls && stalls.length > 0 ? (
							stalls.map((stall, idx) => (
								<TableRow
									key={stall.id || idx}
									hover
									sx={{
										"&:hover": {
											bgcolor: (theme) =>
												alpha(theme.palette.primary.main, 0.02),
										},
									}}
								>
									<TableCell
										sx={{
											color: "text.secondary",
											fontWeight: 600,
										}}
									>
										{idx + 1}
									</TableCell>
									<TableCell>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											<MeetingRoomTwoToneIcon
												fontSize="small"
												color="primary"
											/>
											<Typography
												variant="body2"
												fontWeight={600}
												color="text.primary"
											>
												{stall.hall_name}
											</Typography>
										</Box>
									</TableCell>
									<TableCell>
										<Chip
											label={stall.stall_no}
											size="small"
											color="primary"
											variant="outlined"
											sx={{
												fontWeight: "bold",
												borderRadius: 1.5,
											}}
										/>
									</TableCell>

									{/* QR Code Thumbnail Column */}
									<TableCell>
										<Tooltip title="Click to view & download Stall Pass / QR Code" arrow>
											<Box
												onClick={() => setSelectedStall(stall)}
												sx={{
													position: "relative",
													width: 44,
													height: 44,
													p: 0.5,
													border: "1px solid",
													borderColor: "grey.300",
													borderRadius: 1.5,
													bgcolor: "#ffffff",
													cursor: "pointer",
													display: "inline-flex",
													alignItems: "center",
													justifyContent: "center",
													transition: "all 0.2s ease-in-out",
													"&:hover": {
														borderColor: "primary.main",
														transform: "scale(1.08)",
														boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
														"& .zoom-overlay": {
															opacity: 1,
														},
													},
												}}
											>
												<img
													src={getStallQrCodeUrl(stall.stall_no, 100)}
													alt={`QR for ${stall.stall_no}`}
													style={{
														width: "100%",
														height: "100%",
														objectFit: "contain",
													}}
													loading="lazy"
												/>
												<Box
													className="zoom-overlay"
													sx={{
														position: "absolute",
														inset: 0,
														bgcolor: "rgba(0, 0, 0, 0.4)",
														borderRadius: 1.5,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														opacity: 0,
														transition: "opacity 0.2s ease",
														color: "#ffffff",
													}}
												>
													<ZoomInTwoToneIcon sx={{ fontSize: 20 }} />
												</Box>
											</Box>
										</Tooltip>
									</TableCell>

									<TableCell>
										<Chip
											label={
												stall.total_scanned_stall ??
												stall.total_scanned_stalls ??
												0
											}
											size="small"
											color="secondary"
											variant="filled"
											sx={{
												fontWeight: 700,
												borderRadius: 1.5,
												minWidth: 32,
											}}
										/>
									</TableCell>
									<TableCell sx={{ color: "text.secondary" }}>
										{stall.created_at
											? moment(stall.created_at).format("LL")
											: "-"}
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={6} align="center" sx={{ py: 6 }}>
									<StorefrontTwoToneIcon
										sx={{
											fontSize: 48,
											color: "text.disabled",
											mb: 1,
										}}
									/>
									<Typography
										variant="h5"
										color="text.secondary"
										fontWeight={600}
									>
										No Stalls Found
									</Typography>
									<Typography variant="body2" color="text.disabled">
										No stalls have been allocated to this exhibitor yet.
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Exhibitor Stall Pass & QR Code Modal */}
			<Dialog
				open={Boolean(selectedStall)}
				onClose={() => setSelectedStall(null)}
				maxWidth="sm"
				fullWidth
				disableEnforceFocus
				PaperProps={{
					sx: {
						borderRadius: 3,
						overflow: "hidden",
						boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
					},
				}}
			>
				{selectedStall && (
					<>
						<DialogTitle
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								py: 2,
								px: 3,
								bgcolor: alpha(theme.palette.primary.main, 0.04),
								borderBottom: "1px solid",
								borderColor: "divider",
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
								<Box
									sx={{

										borderRadius: "70%",
										bgcolor: alpha(theme.palette.primary.main, 0.12),
										color: "primary.main",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<BadgeTwoToneIcon fontSize="small" />
								</Box>
								<Box>
									<Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>
										Exhibitor Stall Pass
									</Typography>

								</Box>
							</Box>
							<IconButton
								size="small"
								onClick={() => setSelectedStall(null)}
								sx={{ color: "text.secondary" }}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</DialogTitle>

						<DialogContent
							sx={{
								py: 2,
								px: { xs: 2, sm: 3 },
								bgcolor: "#f8fafc",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							{/* Stall Pass Card with Middle QR and Bottom Details */}
							<Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
								<ExhibitorStallPassCard
									key={`${selectedStall.id || ""}-${selectedStall.stall_no}`}
									ref={passCardRef}
									stall={selectedStall}
									companyName={selectedStall.exhibitor_name || exhibitorName}
									width={330}
								/>
							</Box>

							{/* Stall Scan Stats */}
							<Box
								sx={{
									display: "inline-flex",
									justifyContent: "center",
									alignItems: "center",
									gap: 0.8,
									mt: 1.2,
									bgcolor: "#ffffff",
									px: 2.2,
									py: 0.6,
									borderRadius: 2,
									border: "1px solid",
									borderColor: "grey.200",
									boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
								}}
							>
								<QrCodeScannerTwoToneIcon fontSize="small" color="secondary" />
								<Typography variant="body2" color="text.secondary" fontWeight={600}>
									Total Scans:{" "}
									<Box component="span" sx={{ color: "text.primary", fontWeight: 800 }}>
										{selectedStall.total_scanned_stall ??
											selectedStall.total_scanned_stalls ??
											0}
									</Box>
								</Typography>
							</Box>
						</DialogContent>

						<Divider />

						<DialogActions
							sx={{
								p: 2,
								px: 3,
								justifyContent: "space-between",
								bgcolor: "#ffffff",
								flexWrap: "wrap",
								gap: 1,
							}}
						>
							<Box sx={{ display: "flex", gap: 1 }}>
								<Button
									variant="outlined"
									color="inherit"
									onClick={() => setSelectedStall(null)}
									sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
								>
									Close
								</Button>
								<Button
									variant="text"
									color="secondary"
									startIcon={<QrCode2TwoToneIcon />}
									onClick={() => handleDownloadQR(selectedStall.stall_no)}
									sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
								>
									QR Only
								</Button>
							</Box>

							<Box sx={{ display: "flex", gap: 1.5 }}>
								<Button
									variant="outlined"
									color="primary"
									startIcon={
										isDownloading ? (
											<CircularProgress size={16} color="inherit" />
										) : (
											<DownloadTwoToneIcon />
										)
									}
									onClick={handleDownloadPass}
									disabled={isDownloading}
									sx={{
										textTransform: "none",
										borderRadius: 2,
										fontWeight: 700,
									}}
								>
									Download Pass
								</Button>
								<Button
									variant="contained"
									color="primary"
									startIcon={<PrintTwoToneIcon />}
									onClick={() => handlePrint()}
									sx={{
										textTransform: "none",
										borderRadius: 2,
										fontWeight: 700,
										boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
									}}
								>
									Print Pass
								</Button>
							</Box>
						</DialogActions>
					</>
				)}
			</Dialog>
		</>
	);
};

export default ExhibitorStallsTab;
